/** @jsx jsx */
// eslint-disable-next-line
import React, { useCallback, useState, useMemo } from 'react'
import propTypes from 'prop-types'
import { toggleModal, addReminder, deleteReminder } from '../actions/calendar'
import { css, jsx } from '@emotion/core'
import { palette, boxShadow } from '../constants'

const styles = {
  container: css`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${palette.backdrop};
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  content: css`
    width: 450px;
    height: 255px;
    background: ${palette.white};
    border-radius: 4px;
    box-shadow: ${boxShadow};
    padding: 20px;
    position: relative;
    z-index: 2;
  `,
  close: css`
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
  `,
  button: css`
    background: ${palette.red};
    padding: 4px 15px 6px 15px;
    border-radius: 3px;
    border: 0;
    box-shadow: ${boxShadow};
    color: ${palette.white};
    font-size: 15px;
    position: absolute;
    bottom: 20px;
    cursor: pointer;
    width: 75px;
    height: 30px;
  `,
  modalBody: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-top: 20px;
  `,
  textArea: css`
    height: 80px;
    width: 100%;
    padding: 10px;
    border: 1px solid ${palette.grey};
    border-radius: 4px;
  `,
  contentAttr: css`
    display: flex;
    margin-bottom: 20px;
  `,
  labelsWrapper: css`
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-right: 20px;

    &.last {
      margin-right: 0;
    }

    label {
      font-size: 13px;
      margin-bottom: 5px;
    }

    input,
    select {
      width: 100%;
      border-radius: 3px;
      border: 1px solid ${palette.grey};
      padding: 3px;
      color: ${palette.black};

      &[type='color'] {
        padding: 1px 3px;
        height: 28px;
        background: ${palette.white};
      }
    }
  `,
}

export const startTimeMin = '09:00'
const startTimeMax = '17:00'
export const endTimeMax = '18:00'
const timeStep = '3600'

// Generates random id
const guid = () => {
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)
  return `${s4()}-${s4()}`
}

const calcMinEndTime = startTimeVal => {
  const time = startTimeVal || startTimeMin
  const start = Number(time.split(':')[0]) + 1
  return `${String(start).padStart(2, '0')}:00`
}

const dayInMonths = date => {
  const year = Number(date.split('-')[0])
  const month = Number(date.split('-')[1])
  const days = new Date(year, month, 0).getDate()
  return [...Array(days).keys()].map(i => i + 1)
}

const dayHoursList = (start, end) => {
  const startNumber = Number(start.split(':')[0])
  const endNumber = Number(end.split(':')[0])
  return [...Array(startNumber + 2).keys()].map(i => String(i + (endNumber - startNumber)).padStart(2, '0'))
}

// We are using the time slots from 09:00 to 18:00
function ReminderModal({ isEditing, date, reminderEditing }) {
  const closeModal = useCallback(() => toggleModal(false), [])
  const [color, setColor] = useState(() => (isEditing ? reminderEditing.color : palette.red))
  const [startTime, setStartTime] = useState(() => (isEditing ? reminderEditing.startTime : ''))
  const [endTime, setEndTime] = useState(() => (isEditing ? reminderEditing.endTime : ''))
  const [description, setDescription] = useState(() => (isEditing ? reminderEditing.description : ''))
  const [day, setDay] = useState(() => (isEditing ? reminderEditing.date.split('-')[2] : ''))

  const deleteItem = useCallback(
    e => {
      e.preventDefault()
      e.stopPropagation()
      deleteReminder(reminderEditing)
    },
    [reminderEditing]
  )
  const submitForm = useCallback(
    e => {
      e.preventDefault()
      const reminder = {
        color,
        startTime,
        endTime,
        description,
        id: isEditing ? reminderEditing.id : guid(),
        date: `${date}-${day}`,
      }
      const reminderParam = { reminder }
      // If the reminder is being edited and we change the day
      // The reminder needs to be deleted from previous day
      if (isEditing && `${date}-${day}` !== reminderEditing.date) {
        reminderParam.shouldDeleteDate = reminderEditing.date
      }
      addReminder(reminderParam)
      closeModal()
    },
    [color, startTime, endTime, description, day, isEditing, reminderEditing, date]
  )

  const daysOptions = useMemo(() => dayInMonths(date), [date])

  return (
    <div css={styles.container} onClick={closeModal} role="button">
      <form method="post" onSubmit={submitForm}>
        <div role="button" css={styles.content} onClick={e => e.stopPropagation()}>
          <span role="button" css={styles.close} onClick={closeModal}>
            X
          </span>
          <div css={styles.modalBody}>
            <div css={styles.contentAttr}>
              <div css={styles.labelsWrapper}>
                <label>Day</label>
                <select value={day} onChange={e => setDay(e.target.value)} required>
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {daysOptions.map(i => (
                    <option key={i} value={`${i}`}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
              <div css={styles.labelsWrapper}>
                <label>Start time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={e => {
                    setStartTime(e.target.value)
                    if (e.target.value === startTimeMax) {
                      setEndTime(endTimeMax)
                    }
                  }}
                  list="startTimeList"
                  min={startTimeMin}
                  max={startTimeMax}
                  step={timeStep} // 60 min
                  required
                />
                <datalist id="startTimeList">
                  {dayHoursList(startTimeMin, startTimeMax).map(i => (
                    <option key={i} value={`${i}:00`} />
                  ))}
                </datalist>
              </div>
              <div css={styles.labelsWrapper}>
                <label>End time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  list="endTimeList"
                  min={calcMinEndTime(startTime)} // Min start time needs to be greater than the start time
                  max={endTimeMax}
                  step={timeStep} // 60 min
                  required
                />
                <datalist id="endTimeList">
                  {dayHoursList(startTime, startTimeMax).map(i => (
                    <option key={i} value={`${i}:00`} />
                  ))}
                </datalist>
              </div>
              <div css={styles.labelsWrapper} className="last">
                <label>Color</label>
                <input type="color" value={color} onChange={e => setColor(e.target.value)} required />
              </div>
            </div>
            <textarea
              css={styles.textArea}
              placeholder="Add description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength="30"
              required
            />
          </div>
          {isEditing && (
            <button
              css={css`
                ${styles.button};
                right: 115px;
              `}
              onClick={deleteItem}
              className="hover"
            >
              Delete
            </button>
          )}
          <button
            css={css`
              ${styles.button};
              right: 20px;
            `}
            className="hover"
            type="submit"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  )
}

ReminderModal.propTypes = {
  isEditing: propTypes.bool.isRequired,
  date: propTypes.string.isRequired,
  reminderEditing: propTypes.oneOfType([propTypes.object, propTypes.bool]),
}

export default ReminderModal
